import type { InputAction, Lane } from "../data/model";
import { flight as F, jumpVelocity, motor as M, world } from "../data/tuning";

/** The car's movement states, the original runner's: idle · running · jumping · falling · diving · rolling · flying · dead. */
export type MotorState = "idle" | "running" | "jumping" | "falling" | "diving" | "rolling" | "flying" | "dead";

/** What boards and power-ups change about the motor (the original's ability blueprints). */
export interface Abilities {
  jumpHeightMultiplier: number;
  /** extra jumps allowed in the air (Double Jump: the original's jump limit 2 = one extra) */
  airJumps: number;
  /** height of a jump made in the air (AirJumpHeight: 4 m, or 3.6 m while the high jump is on) */
  airJumpHeight: number;
  laneChangeSeconds: number;
  /** [at start speed, at end speed] added to the run speed (Speed / Super Speed boards) */
  speedAddition: readonly [number, number] | null;
  speedMultiplier: number;
  gravityMultiplier: number;
  /** Stay Low: always at rolled height */
  stayLow: boolean;
  glide: boolean;
  noCornerCollision: boolean;
  noLowerCollision: boolean;
}

export const defaultAbilities = (): Abilities => ({
  jumpHeightMultiplier: 1,
  airJumps: 0,
  airJumpHeight: M.airJumpHeight,
  laneChangeSeconds: M.laneChangeSeconds,
  speedAddition: null,
  speedMultiplier: 1,
  gravityMultiplier: 1,
  stayLow: false,
  glide: false,
  noCornerCollision: false,
  noLowerCollision: false
});

export interface Support {
  /** height of the surface under the car (road = 0) */
  height: number;
  kind: "road" | "roof" | "ramp" | "platform" | "none";
}

export interface MotorEvents {
  jump(air: boolean): void;
  land(hard: boolean): void;
  lane(): void;
  roll(): void;
  /** a swipe into the wall (the original's boundary impact: a stumble, once per move) */
  boundary?(dir: -1 | 1): void;
}

export interface Flight {
  height: number;
  /** multiplies the run speed (jetpack ×2) … */
  speedMultiplier: number;
  /** … or replaces it (headstart) */
  fixedSpeed?: number;
  climbSeconds: number;
  kind: "jetpack" | "headstart";
}

/**
 * Lanes, speed, hops, dives, rolls, flight and the input queue. No collision here: the world tells the motor what
 * surface is under the car; the motor tells the world where the car is.
 */
export class Motor {
  state: MotorState = "idle";
  /** distance along the road, metres */
  z = 0;
  x = 0;
  y = 0;
  vy = 0;
  lane: Lane = 0;
  speed = M.startSpeed;
  /** seconds the run has been going (the speed ramps with it) */
  elapsed = 0;
  abilities: Abilities = defaultAbilities();
  flight: Flight | null = null;
  /** the pogo hop: where its 60 m rise began (null = no hop); after the rise it falls freely until it lands */
  private pogoFrom: number | null = null;
  private pogoFalling = false;
  private pogoStartY = 0;
  // the lane change, the original's LaneStateInfo: from where (in lanes), how long, how far driven since it began
  private changing = false;
  private laneSource = 0;
  private laneElapsed = 0;
  private laneDuration = 1;
  private laneRun = 0;
  private hadBoundaryImpact = false;
  /** +1 for every lane change started (collision rules count hits "once per entity per move") */
  moveId = 0;
  /** CanAccelerateForward = false: pressed against a minor obstacle until the next jump or lane change */
  blocked = false;
  private rollTime = 0;
  private coyote = 0;
  /** jumps since the car last touched the ground (the original's JumpCount): the first uses the jump height, the rest the air-jump height */
  private jumpCount = 0;
  /** seconds since the glide began (it begins when the fall does, with the glide ability on); −1 when not gliding */
  private glideClock = -1;
  private queue: { action: InputAction; until: number } | null = null;
  private slow: { factor: number; until: number } | null = null;
  private boost: { add: number; until: number } | null = null;
  private time = 0;
  private wasOnSurface = true;

  get rolled(): boolean {
    return this.state === "rolling" || this.abilities.stayLow;
  }

  get colliderHeight(): number {
    return M.colliderHeight * (this.rolled ? M.rollHeightFactor : 1);
  }

  get airborne(): boolean {
    return this.state === "jumping" || this.state === "falling" || this.state === "diving";
  }

  get laneProgress(): number {
    return this.changing ? Math.min(1, this.laneElapsed / this.laneDuration) : 1;
  }

  /** a lane change is running */
  get changingLane(): boolean {
    return this.changing;
  }

  /** the direction of the running lane change (−1 left, +1 right, 0 none) */
  get laneDirection(): number {
    return this.changing ? Math.sign(this.lane - this.laneSource) : 0;
  }

  /** the run's own speed on the ramp (no flight, pads or boards): the original's ForwardSpeedTarget at the base abilities */
  get rampSpeed(): number {
    return M.startSpeed + (M.endSpeed - M.startSpeed) * Math.min(1, this.elapsed / M.speedRampSeconds);
  }

  /** a pogo arc is running */
  get inArc(): boolean {
    return this.pogoFrom !== null || this.pogoFalling;
  }

  /** A trip over an obstacle's edge: set on its top, vertical speed gone, running (LowerFrontal). */
  landOn(top: number): void {
    this.y = top;
    this.vy = 0;
    this.jumpCount = 0;
    this.glideClock = -1;
    if (this.state !== "flying") this.state = this.state === "rolling" ? "rolling" : "running";
  }

  /** The hop after a board or shield takes a crash (the original's auto-revive jump). */
  forceJump(events: MotorEvents): void {
    if (this.state === "flying" || this.state === "dead" || this.state === "idle") return;
    this.blocked = false;
    this.jump(M.jumpHeight, false, events);
  }

  /** a speed pad is pushing the car */
  get boosting(): boolean {
    return this.boost !== null && this.time < this.boost.until;
  }

  /** where the lane change started, in lanes */
  get laneOrigin(): number {
    return this.laneSource;
  }

  start(): void {
    if (this.state === "idle") this.state = "running";
  }

  kill(): void {
    this.state = "dead";
    this.flight = null;
    this.queue = null;
  }

  /** Back to running after a continue: on the road, standing still in the lane. */
  resurrect(): void {
    this.state = "running";
    this.vy = 0;
    this.flight = null;
    this.pogoFrom = null;
    this.pogoFalling = false;
    this.rollTime = 0;
    this.jumpCount = 0;
    this.glideClock = -1;
    this.changing = false;
    this.blocked = false;
    this.hadBoundaryImpact = false;
    this.x = this.lane * world.laneWidth;
  }

  update(dt: number, support: Support, events: MotorEvents): void {
    this.time += dt;
    if (this.state === "idle" || this.state === "dead") return;
    this.elapsed += dt;
    this.updateSpeed();

    // lanes — linear over a duration fixed when the change began (ApplyLaneChangeVelocity)
    if (this.changing) {
      this.laneRun += this.speed * dt;
      this.laneElapsed += dt;
      const t = Math.min(1, this.laneElapsed / this.laneDuration);
      this.x = (this.laneSource + (this.lane - this.laneSource) * t) * world.laneWidth;
      if (t >= 1) { this.x = this.lane * world.laneWidth; this.changing = false; }
    }
    if (!this.blocked) this.z += this.speed * dt;

    // timers
    if (this.rollTime > 0) {
      this.rollTime = Math.max(0, this.rollTime - dt);
      if (this.rollTime === 0 && this.state === "rolling") this.state = "running";
    }
    if (this.coyote > 0) this.coyote = Math.max(0, this.coyote - dt);

    // vertical
    if (this.state === "flying" && this.flight) {
      const k = 1 - Math.exp(-dt * 3 / this.flight.climbSeconds);
      this.y += (this.flight.height - this.y) * k;
      this.vy = 0;
    } else if (this.pogoFrom !== null && this.state === "jumping") {
      // the pogo rise: an ease-out curve to 32 m over 60 m of road, then gravity takes over
      const d = this.z - this.pogoFrom;
      if (d < F.pogoDistance) {
        const k = 1 - d / F.pogoDistance;
        const rise = Math.max(0, F.pogoHeight - this.pogoStartY);         // from where you are (a roof too) up to 32 m
        this.y = this.pogoStartY + rise * (1 - k * k);
        this.vy = ((2 * rise) / F.pogoDistance) * k * this.speed;
      } else {
        this.y = Math.max(F.pogoHeight, this.pogoStartY);
        this.vy = 0;
        this.pogoFrom = null;
        this.pogoFalling = true;
      }
    } else if (this.airborne) {
      // the glide (Smooth Drift board): from the moment the fall starts, gravity follows the glide curve from almost
      // nothing up to 5 m/s² over 1.5 s and stays there until the car lands; a jump or a dive ends it
      if (!this.abilities.glide || this.state === "diving" || this.pogoFalling) this.glideClock = -1;
      else if (this.glideClock < 0 && this.vy <= 0) this.glideClock = 0;
      let g = M.gravity * this.abilities.gravityMultiplier;
      if (this.glideClock >= 0) {
        this.glideClock += dt;
        g = Math.max(M.glideMinGravity, M.glideGravity * glideShare(Math.min(1, this.glideClock / M.glideSeconds)));
      }
      this.vy -= g * dt;
      this.y += this.vy * dt;
      // a ramp's slope rises faster than a hop (13.9–27.8 m/s against 17.9 m/s at take-off): it pushes the car up and the hop
      // carries on from the surface, instead of the arc sinking into the slope (which read as hitting the ramp)
      if (support.kind === "ramp" && this.vy > 0 && this.y < support.height) this.y = support.height;
      if (this.y <= support.height && this.vy <= 0) {
        const hard = this.state === "diving" || this.pogoFalling;
        this.y = support.height;
        this.vy = 0;
        this.pogoFrom = null;
        this.pogoFalling = false;
        this.glideClock = -1;
        this.jumpCount = 0;
        if (this.state === "diving") {
          this.state = "rolling";
          this.rollTime = M.rollSeconds;
          events.roll();
        } else {
          this.state = "running";
        }
        events.land(hard);
      }
    } else {
      // on a surface: follow it, step up small ledges, climb ramps no faster than the limit, fall off edges
      if (support.kind === "none" || this.y > support.height + M.stepUp) {
        // driving off an edge (a truck roof's end): the jump still works for 0.15 s (GroundedTimeExtensionForForgivingJumps)
        this.coyote = M.forgivingJumpSeconds;
        this.state = "falling";
        this.vy = 0;
      } else if (support.height > this.y) {
        this.y = Math.min(support.height, this.y + Math.max(M.surfaceMaxUpwardsSpeed, this.speed * 1.2) * dt);
        this.jumpCount = 0;
      } else {
        this.y = support.height;
        this.jumpCount = 0;
      }
    }
    this.wasOnSurface = support.kind !== "none";

    // the queue: an input that could not run keeps trying for half a second
    if (this.queue) {
      if (this.time > this.queue.until) this.queue = null;
      else if (this.execute(this.queue.action, events)) this.queue = null;
    }
  }

  /**
   * The original's input slot: a new gesture replaces whatever was waiting; it runs now if it can, otherwise it keeps
   * trying for 0.5 s (every action, lane changes too). Returns false only when the car cannot take input at all.
   */
  tryAction(action: InputAction, events: MotorEvents): boolean {
    if (this.state === "dead" || this.state === "idle") return false;
    this.queue = null;
    if (this.execute(action, events)) return true;
    this.queue = { action, until: this.time + M.inputQueueSeconds };
    return true;
  }

  /** A corner hit while moving into the obstacle: the change reverses (ChangeLane(−dir), a reversal). */
  bounceBack(events?: MotorEvents): boolean {
    if (!this.changing) return false;
    const dir = this.laneDirection;
    if (dir === 0 || this.laneRun < M.minTravelBetweenLaneChanges) return false;
    this.reverse(events);
    return true;
  }

  /** A side hit: back to the lane being left, at 0.5 s per lane width (CancelLaneChanging). */
  cancelLaneChange(): void {
    if (!this.changing) return;
    const target = Math.round(this.laneSource) as Lane;
    this.laneSource = this.x / world.laneWidth;
    this.lane = target;
    this.laneElapsed = 0;
    this.laneDuration = Math.max(0.01, M.sideImpactReturnSeconds * Math.abs(target - this.laneSource));
  }

  /** Keep a roll going a moment longer (something low is overhead). */
  holdRoll(): void {
    if (this.state === "rolling") this.rollTime = Math.max(this.rollTime, 0.05);
  }

  /** Pressed against a minor obstacle (a frontal hit that is not fatal): no forward motion until the next jump or lane change. */
  block(): void {
    this.blocked = true;
  }

  startFlight(flight: Flight): void {
    this.flight = flight;
    this.state = "flying";
    this.vy = 0;
    this.pogoFrom = null;
    this.pogoFalling = false;
  }

  endFlight(): void {
    if (this.state !== "flying") return;
    this.flight = null;
    this.state = "falling";
    this.vy = 0;
  }

  /** The pogo hop: rises 32 m over 60 m of road, then falls freely (PogoStickPowerEffect). Jumps and the board are off meanwhile. */
  pogo(): void {
    if (this.state === "flying") return;                  // a flight is not turned into a hop
    this.pogoFrom = this.z;
    this.pogoStartY = this.y;
    this.pogoFalling = false;
    this.vy = 0;
    this.state = "jumping";
    this.glideClock = -1;
  }

  /** Slow the run for a moment (a stumble, a slow pad, treadmilling against a flank). */
  slowDown(factor: number, seconds: number): void {
    this.slow = { factor, until: this.time + seconds };
  }

  /** Add speed for a moment (a boost pad). */
  boostUp(add: number, seconds: number): void {
    this.boost = { add, until: this.time + seconds };
  }

  private execute(action: InputAction, events: MotorEvents): boolean {
    switch (action) {
      case "left":
      case "right": {
        const dir = action === "left" ? -1 : 1;
        if (this.changing) {
          // the same way again waits for this change to finish (stays queued); the other way reverses after 0.3 m
          if (dir === this.laneDirection) return false;
          if (this.laneRun < M.minTravelBetweenLaneChanges) return false;
          this.reverse(events);
          return true;
        }
        const next = this.lane + dir;
        if (next < -1 || next > 1) {
          // into the wall: a bump (a stumble) once per move, swallowed after that; nothing while flying
          if (!this.hadBoundaryImpact) {
            this.hadBoundaryImpact = true;
            if (this.state !== "flying") events.boundary?.(dir as -1 | 1);
          }
          return true;
        }
        if (this.state === "rolling") { this.state = "running"; this.rollTime = 0; }     // a lane change ends the roll
        this.laneSource = this.x / world.laneWidth;
        this.lane = next as Lane;
        this.laneElapsed = 0;
        this.laneDuration = this.laneSeconds();
        this.laneRun = 0;
        this.changing = true;
        this.startMove(events);
        return true;
      }
      case "jump": {
        if (this.state === "flying" || this.inArc) return false;      // no jumps in flight or on the pogo arc
        // CanJump: on the ground (or just off an edge) with no jump made yet — or, with a double jump, while jumps are
        // left. Off an edge without a jump, a double jump gives a full hop and then the air hop.
        const limit = 1 + this.abilities.airJumps;
        const onGround = !this.airborne || this.coyote > 0;
        if (!((onGround && this.jumpCount === 0) || (limit > 1 && this.jumpCount < limit))) return false;
        this.blocked = false;          // a jump frees a car pressed against a minor obstacle (the original swallows that first jump; ours hops)
        const air = this.jumpCount > 0;
        this.jump(air ? this.abilities.airJumpHeight : M.jumpHeight * this.abilities.jumpHeightMultiplier, air, events);
        return true;
      }
      case "roll": {
        if (this.state === "flying") return false;
        if (this.airborne) {
          if (this.state === "diving") return true;
          this.vy = Math.min(this.vy, M.diveVelocity);
          this.state = "diving";
          this.glideClock = -1;
          return true;
        }
        if (this.state === "rolling") return true;                   // down while rolling: ignored, the roll is not restarted
        this.state = "rolling";
        this.rollTime = M.rollSeconds;
        events.roll();
        return true;
      }
      default:
        return true;
    }
  }

  private jump(height: number, air: boolean, events: MotorEvents): void {
    this.vy = jumpVelocity(height, M.gravity * this.abilities.gravityMultiplier);
    this.state = "jumping";
    this.rollTime = 0;
    this.coyote = 0;
    this.pogoFrom = null;
    this.pogoFalling = false;
    this.jumpCount += 1;
    this.glideClock = -1;          // a new rise: the glide starts again when this hop starts to fall
    events.jump(air);
  }

  /** GetLaneChangeDuration: the ability's time divided by how much faster than the minimum speed the car goes (0.27 s → 0.135 s at top speed). */
  private laneSeconds(): number {
    const base = this.state === "flying" ? M.laneChangeSecondsFlying : this.abilities.laneChangeSeconds;
    const minSpeed = (M.startSpeed + (this.abilities.speedAddition?.[0] ?? 0)) * this.abilities.speedMultiplier;
    return base / Math.max(1, this.speed / Math.max(1, minSpeed));
  }

  /** A reversal mid-change: back to the lane being left, the time scaled by the distance still to go. */
  private reverse(events?: MotorEvents): void {
    const target = Math.round(this.laneSource) as Lane;
    const d = this.laneSeconds();
    this.laneSource = this.x / world.laneWidth;
    this.lane = target;
    this.laneElapsed = 0;
    this.laneDuration = Math.max(0.01, d * Math.abs(target - this.laneSource));
    this.changing = true;
    if (events) this.startMove(events);
  }

  /** Every lane change (fresh or reversed) resets the per-move bookkeeping and frees a blocked car. */
  private startMove(events: MotorEvents): void {
    this.hadBoundaryImpact = false;
    this.blocked = false;
    this.moveId += 1;
    events.lane();
  }

  private updateSpeed(): void {
    const ramp = Math.min(1, this.elapsed / M.speedRampSeconds);
    let speed = M.startSpeed + (M.endSpeed - M.startSpeed) * ramp;
    if (this.abilities.speedAddition) speed += this.abilities.speedAddition[0] + (this.abilities.speedAddition[1] - this.abilities.speedAddition[0]) * ramp;
    if (this.boost && this.time < this.boost.until) speed += this.boost.add; else this.boost = null;
    speed *= this.abilities.speedMultiplier;
    if (this.slow && this.time < this.slow.until) speed *= this.slow.factor; else this.slow = null;
    if (this.flight) speed = this.flight.fixedSpeed ?? speed * this.flight.speedMultiplier;
    this.speed = speed;
  }
}

/** The glide curve (a Hermite spline through ability_GliderDefault's keys): the share of glide gravity at t = 0…1. */
export function glideShare(t: number): number {
  const keys = M.glideCurve;
  if (t <= keys[0][0]) return keys[0][1];
  for (let i = 0; i < keys.length - 1; i++) {
    const [t0, v0, , out0] = keys[i];
    const [t1, v1, in1] = keys[i + 1];
    if (t > t1) continue;
    const d = t1 - t0;
    const s = (t - t0) / d;
    const s2 = s * s, s3 = s2 * s;
    return (2 * s3 - 3 * s2 + 1) * v0 + (s3 - 2 * s2 + s) * out0 * d + (-2 * s3 + 3 * s2) * v1 + (s3 - s2) * in1 * d;
  }
  return keys[keys.length - 1][1];
}
